import React, { useState, useRef, useEffect } from 'react';
import { Terminal } from 'lucide-react';
import { generateSubnetReport } from '../services/reportService';

interface TerminalHistory {
  input: string;
  output: string;
}

export function SubnetTerminal() {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<TerminalHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new content is added
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setLoading(true);
    try {
      const response = await generateSubnetReport(input.trim());
      setHistory(prev => [...prev, { input, output: response }]);
      setInput('');
    } catch (error) {
      setHistory(prev => [...prev, { 
        input, 
        output: 'Error: Unable to process your request. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
      <div className="flex items-center space-x-3 mb-6">
        <Terminal className="w-6 h-6 text-red-600" />
        <h2 className="text-xl font-bold">Subnet Analytics Terminal</h2>
      </div>

      <div 
        ref={terminalRef}
        className="bg-black/60 rounded-lg p-4 h-[400px] mb-4 overflow-y-auto font-mono text-sm"
      >
        <div className="space-y-4">
          {history.map((entry, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-400">
                <span className="text-red-600">❯</span>
                <span>{entry.input}</span>
              </div>
              <div className="text-gray-200 whitespace-pre-wrap pl-4">
                {entry.output}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center space-x-2 text-gray-400">
              <span className="text-red-600">❯</span>
              <span className="animate-pulse">Processing request...</span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter your query (e.g., 'What subnets are whales buying?', 'What subnets are whales silently accumulating?')"
          className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-4 pl-6 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
          disabled={loading}
        />
        <span className="absolute left-2 top-2.5 text-red-600">❯</span>
      </form>

      <div className="mt-4 text-sm text-gray-400">
        <p>Example queries:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>"What subnets are whales buying?"</li>
          <li>"What subnets are whales selling?"</li>
          <li>"Which subnets are seeing selling pressure?"</li>
          <li>"Who are the top active wallets?"</li>
          <li>"What subnets are whales silently accumulating?"</li>
          <li>"Show emissions for Subnet 64"</li>
        </ul>
      </div>
    </div>
  );
}